import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Input, Badge } from "./ui";
import { num, money } from "../lib/holdingsMath";
import { Landmark, Plus, Trash2, Save, Download, AlertTriangle, Loader2, CalendarClock, FileText, Users } from "lucide-react";

const YEAR = new Date().getUTCFullYear();

export default function SutaManager() {
  const [states, setStates] = useState([]);
  const [year, setYear] = useState(YEAR);
  const [review, setReview] = useState({ needs_review: false, prev_year: null });
  const [cfg, setCfg] = useState({ futa_enabled: false, futa_rate_pct: 0.6, futa_wage_base: 7000 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [add, setAdd] = useState({ state_code: "", state_name: "", wage_base: "", rate_pct: "", effective_date: "", filing_frequency: "", account_number: "" });
  const [report, setReport] = useState(null);
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [rptQuarter, setRptQuarter] = useState("");
  const [rptYear, setRptYear] = useState(YEAR);
  const [ytd, setYtd] = useState(null);

  const loadYtd = () => client.get(`/suta/ytd?year=${year}`).then((r) => setYtd(r.data.items || [])).catch(() => {});

  const load = () => client.get(`/suta/states?year=${year}`).then((r) => {
    setStates(r.data.states || []);
    setReview({ needs_review: r.data.needs_review, prev_year: r.data.prev_year });
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  useEffect(() => { client.get("/suta/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch }; setCfg(next);
    try { const { data } = await client.put("/suta/config", patch); setCfg(data); } catch (e) { setMsg(apiErr(e)); }
  };
  const carryForward = async () => {
    if (!review.prev_year) return;
    setBusy(true);
    try { const { data } = await client.post(`/suta/states/roll-year?src=${review.prev_year}&dst=${year}`); setMsg(`Carried ${data.added} state(s) forward from ${data.src} — verify each for ${year}.`); load(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const seed = async () => {
    setBusy(true); setMsg("Seeding starter US states…");
    try { const { data } = await client.post(`/suta/states/seed?year=${year}`); setMsg(`Added ${data.added} state(s). Verify each wage base and enter your assigned rate.`); load(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  const addState = async () => {
    if (!add.state_code.trim()) { setMsg("Enter a state code (e.g. CA)."); return; }
    setBusy(true); setMsg("");
    try {
      await client.post("/suta/states", { state_code: add.state_code, state_name: add.state_name, year,
        wage_base: num(add.wage_base), rate_pct: num(add.rate_pct), effective_date: add.effective_date,
        filing_frequency: add.filing_frequency, account_number: add.account_number });
      setAdd({ state_code: "", state_name: "", wage_base: "", rate_pct: "", effective_date: "", filing_frequency: "", account_number: "" }); load();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  const saveRow = async (s, patch) => {
    const next = { ...s, ...patch };
    setStates((prev) => prev.map((x) => (x.id === s.id ? next : x)));
    try { await client.post("/suta/states", { id: s.id, state_code: s.state_code, state_name: s.state_name,
      year: s.year, wage_base: num(next.wage_base), rate_pct: num(next.rate_pct), note: s.note,
      effective_date: next.effective_date || "", filing_frequency: next.filing_frequency || "", account_number: next.account_number || "" }); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const del = async (id) => { try { await client.delete(`/suta/states/${id}`); load(); } catch (e) { /* noop */ } };

  const rptParams = () => (rptQuarter ? { year: rptYear, quarter: rptQuarter } : { frm: frm || undefined, to: to || undefined });
  const runReport = async () => {
    try { const { data } = await client.get("/suta/report", { params: rptParams() }); setReport(data); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const downloadFile = async (ext, mime, name) => {
    try {
      const res = await client.get(`/suta/report.${ext}`, { params: rptParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: mime })); const a = document.createElement("a");
      a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const noRate = states.some((s) => num(s.rate_pct) <= 0);
  const _now = new Date();
  const _q = Math.floor(_now.getUTCMonth() / 3);
  const _qEnd = new Date(Date.UTC(_now.getUTCFullYear(), _q * 3 + 3, 0));
  const daysToQEnd = Math.ceil((_qEnd - _now) / 864e5);
  const showQtrReminder = daysToQEnd <= 14 && daysToQEnd >= 0;

  return (
    <div className="rounded-xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="suta-manager">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Landmark size={16} className="text-[var(--primary)]" />
        <h3 className="font-head text-base">SUTA — State Unemployment Tax</h3>
        <Badge>employer-paid</Badge>
        <label className="ms-auto flex items-center gap-1 text-xs opacity-70">Year
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value || YEAR, 10))} data-testid="suta-year"
            className="w-20 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" />
        </label>
      </div>
      <p className="mb-3 text-xs opacity-60">
        Employer state unemployment tax, applied to each payee's wages up to that state's annual <b>wage base</b>, at
        <b> your assigned rate</b>. Tracked year-to-date per person per state. You maintain these figures — they are not auto-updated.
      </p>

      <div className="mb-3 flex items-start gap-2 rounded-md bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400" data-testid="suta-verify-note">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Seeded wage bases are <b>approximate starting points — verify each for {year}</b>. SUTA rates are employer-specific, so <b>enter your own assigned rate</b> for each state.</span>
      </div>

      {review.needs_review && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-blue-500/10 p-2 text-xs text-blue-700 dark:text-blue-300" data-testid="suta-year-reminder">
          <CalendarClock size={14} className="shrink-0" />
          <span>New year: no states configured for <b>{year}</b>. Carry last year's figures forward, then review each.</span>
          <Button variant="subtle" onClick={carryForward} disabled={busy} data-testid="suta-carry-forward" className="ms-auto">Carry forward from {review.prev_year}</Button>
        </div>
      )}

      {showQtrReminder && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-emerald-500/10 p-2 text-xs text-emerald-700 dark:text-emerald-300" data-testid="suta-quarter-reminder">
          <CalendarClock size={14} className="shrink-0" />
          <span>Quarter Q{_q + 1} ends in <b>{daysToQEnd} day{daysToQEnd === 1 ? "" : "s"}</b> — run and file your SUTA/FUTA remittance report.</span>
          <Button variant="subtle" className="ms-auto" data-testid="suta-quarter-run"
            onClick={() => { setRptQuarter(String(_q + 1)); setRptYear(_now.getUTCFullYear()); setTimeout(runReport, 50); }}>Run Q{_q + 1} report</Button>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-black/10 p-2 dark:border-white/10" data-testid="futa-config">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!cfg.futa_enabled} onChange={(e) => saveCfg({ futa_enabled: e.target.checked })} data-testid="futa-enabled" />
          Add Federal Unemployment (FUTA)
        </label>
        <label className="flex items-center gap-1 text-xs opacity-70">Rate %
          <input type="number" step="0.01" defaultValue={cfg.futa_rate_pct} onBlur={(e) => saveCfg({ futa_rate_pct: num(e.target.value) })} data-testid="futa-rate"
            className="w-16 rounded-md border border-black/15 bg-white/60 px-2 py-1 text-end dark:border-white/15 dark:bg-black/30" />
        </label>
        <label className="flex items-center gap-1 text-xs opacity-70">Wage base
          <input type="number" defaultValue={cfg.futa_wage_base} onBlur={(e) => saveCfg({ futa_wage_base: num(e.target.value) })} data-testid="futa-base"
            className="w-24 rounded-md border border-black/15 bg-white/60 px-2 py-1 text-end dark:border-white/15 dark:bg-black/30" />
        </label>
        <span className="text-[11px] opacity-50">Flat federal employer tax per employee up to the wage base (typically 0.6% on first $7,000).</span>
      </div>

      {states.length === 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="suta-empty">
          <span className="text-sm opacity-70">No states configured for {year}.</span>
          <Button variant="subtle" onClick={seed} disabled={busy} data-testid="suta-seed">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Seed starter US states</Button>
        </div>
      ) : (
        <div className="mb-3 overflow-x-auto" data-testid="suta-states-table">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-black/10 text-xs opacity-60 dark:border-white/10">
              <th className="p-2 text-start">State</th><th className="p-2 text-end">Wage base</th>
              <th className="p-2 text-end">Rate %</th><th className="p-2 text-start">Effective</th>
              <th className="p-2 text-start">Filing freq</th><th className="p-2 text-start">Acct #</th><th className="p-2"></th>
            </tr></thead>
            <tbody>
              {states.map((s) => (
                <tr key={s.id} className="border-b border-black/5 dark:border-white/5" data-testid={`suta-row-${s.state_code}`}>
                  <td className="p-2"><b>{s.state_code}</b> <span className="opacity-60">{s.state_name}</span></td>
                  <td className="p-2 text-end">
                    <input type="number" defaultValue={s.wage_base} onBlur={(e) => saveRow(s, { wage_base: e.target.value })}
                      data-testid={`suta-base-${s.state_code}`} className="w-24 rounded-md border border-black/15 bg-white/60 px-2 py-1 text-end dark:border-white/15 dark:bg-black/30" />
                  </td>
                  <td className="p-2 text-end">
                    <input type="number" step="0.01" defaultValue={s.rate_pct} onBlur={(e) => saveRow(s, { rate_pct: e.target.value })}
                      data-testid={`suta-rate-${s.state_code}`} className={`w-20 rounded-md border px-2 py-1 text-end dark:bg-black/30 ${num(s.rate_pct) <= 0 ? "border-amber-500" : "border-black/15 dark:border-white/15"}`} />
                  </td>
                  <td className="p-2">
                    <input type="date" defaultValue={s.effective_date || ""} onBlur={(e) => saveRow(s, { effective_date: e.target.value })}
                      data-testid={`suta-eff-${s.state_code}`} className="w-36 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" />
                  </td>
                  <td className="p-2">
                    <input defaultValue={s.filing_frequency || ""} placeholder="e.g. Quarterly" onBlur={(e) => saveRow(s, { filing_frequency: e.target.value })}
                      data-testid={`suta-freq-${s.state_code}`} className="w-24 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" />
                  </td>
                  <td className="p-2">
                    <input defaultValue={s.account_number || ""} placeholder="acct #" onBlur={(e) => saveRow(s, { account_number: e.target.value })}
                      data-testid={`suta-acct-${s.state_code}`} className="w-28 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" />
                  </td>
                  <td className="p-2 text-end"><button onClick={() => del(s.id)} data-testid={`suta-del-${s.state_code}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {noRate && <p className="mt-1 text-xs text-amber-600">⚠ States with a 0% rate won't compute any SUTA until you enter your assigned rate.</p>}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-black/10 p-2 dark:border-white/10">
        <div className="w-16"><Input label="Code" value={add.state_code} onChange={(e) => setAdd({ ...add, state_code: e.target.value.toUpperCase() })} data-testid="suta-add-code" /></div>
        <div className="flex-1 min-w-[110px]"><Input label="State name" value={add.state_name} onChange={(e) => setAdd({ ...add, state_name: e.target.value })} data-testid="suta-add-name" /></div>
        <div className="w-24"><Input label="Wage base" type="number" value={add.wage_base} onChange={(e) => setAdd({ ...add, wage_base: e.target.value })} data-testid="suta-add-base" /></div>
        <div className="w-16"><Input label="Rate %" type="number" value={add.rate_pct} onChange={(e) => setAdd({ ...add, rate_pct: e.target.value })} data-testid="suta-add-rate" /></div>
        <div className="w-36"><Input label="Effective (optional)" type="date" value={add.effective_date} onChange={(e) => setAdd({ ...add, effective_date: e.target.value })} data-testid="suta-add-eff" /></div>
        <div className="w-24"><Input label="Filing freq" value={add.filing_frequency} onChange={(e) => setAdd({ ...add, filing_frequency: e.target.value })} data-testid="suta-add-freq" /></div>
        <div className="w-24"><Input label="Acct #" value={add.account_number} onChange={(e) => setAdd({ ...add, account_number: e.target.value })} data-testid="suta-add-acct" /></div>
        <Button variant="subtle" onClick={addState} disabled={busy} data-testid="suta-add-btn"><Plus size={14} /> Add / update</Button>
      </div>
      <p className="mb-3 text-[11px] opacity-50">Tip: add a second row for the same state with a later <b>Effective</b> date to apply a mid-year rate change automatically to runs on/after that date.</p>

      <div className="mb-3 rounded-md border border-black/10 p-2 dark:border-white/10" data-testid="suta-ytd-block">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-sm font-medium">Employee year-to-date</span>
          <Button variant="ghost" onClick={loadYtd} data-testid="suta-ytd-load"><Users size={13} /> {ytd ? "Refresh" : "Show"}</Button>
        </div>
        {ytd && (ytd.length === 0 ? <p className="p-1 text-xs opacity-60">No payroll recorded yet for {year}.</p> : (
          <div className="overflow-x-auto" data-testid="suta-ytd-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-black/10 text-xs opacity-60 dark:border-white/10">
                <th className="p-2 text-start">Employee</th><th className="p-2 text-start">State / tax</th>
                <th className="p-2 text-end">YTD wages</th><th className="p-2 text-end">Wage base</th><th className="p-2 text-end">Remaining</th>
              </tr></thead>
              <tbody>
                {ytd.map((r, i) => (
                  <tr key={i} className="border-b border-black/5 dark:border-white/5" data-testid={`suta-ytd-row-${i}`}>
                    <td className="p-2">{r.person_name}</td>
                    <td className="p-2">{r.state_code === "__FUTA__" ? "Federal (FUTA)" : `${r.state_code} ${r.state_name}`}</td>
                    <td className="p-2 text-end">{money(r.ytd_wages)}</td>
                    <td className="p-2 text-end opacity-70">{r.wage_base ? money(r.wage_base) : "—"}</td>
                    <td className={`p-2 text-end font-medium ${r.remaining <= 0 ? "text-emerald-600" : ""}`}>{r.wage_base ? (r.remaining <= 0 ? "capped" : money(r.remaining)) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {msg && <div className="mb-3 text-xs opacity-70" data-testid="suta-msg">{msg}</div>}

      <div className="rounded-md border border-black/10 p-2 dark:border-white/10" data-testid="suta-report-block">
        <div className="mb-2 flex flex-wrap items-end gap-2">
          <span className="text-sm font-medium">Liability report</span>
          <label className="text-xs opacity-70">Quarter
            <select value={rptQuarter} onChange={(e) => setRptQuarter(e.target.value)} data-testid="suta-report-quarter" className="ms-1 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30">
              <option value="">Custom dates</option>
              <option value="1">Q1</option><option value="2">Q2</option><option value="3">Q3</option><option value="4">Q4</option>
            </select>
          </label>
          {rptQuarter ? (
            <label className="text-xs opacity-70">Year <input type="number" value={rptYear} onChange={(e) => setRptYear(parseInt(e.target.value || YEAR, 10))} data-testid="suta-report-year" className="ms-1 w-20 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" /></label>
          ) : (<>
            <label className="text-xs opacity-70">From <input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="suta-report-from" className="ms-1 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" /></label>
            <label className="text-xs opacity-70">To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="suta-report-to" className="ms-1 rounded-md border border-black/15 bg-white/60 px-2 py-1 dark:border-white/15 dark:bg-black/30" /></label>
          </>)}
          <Button variant="ghost" onClick={runReport} data-testid="suta-report-run"><Save size={13} /> Run</Button>
          <Button variant="ghost" onClick={() => downloadFile("csv", "text/csv", "suta_liability.csv")} data-testid="suta-report-csv"><Download size={13} /> CSV</Button>
          <Button variant="ghost" onClick={() => downloadFile("pdf", "application/pdf", "suta_remittance.pdf")} data-testid="suta-report-pdf"><FileText size={13} /> PDF</Button>
        </div>
        {report && (
          <div className="overflow-x-auto" data-testid="suta-report-table">
            {report.states.length === 0 && report.total_futa === 0 ? <p className="p-2 text-xs opacity-60">No SUTA / FUTA recorded for this period.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-black/10 text-xs opacity-60 dark:border-white/10">
                  <th className="p-2 text-start">State / tax</th><th className="p-2 text-end">Taxable wages</th><th className="p-2 text-end">Due</th>
                </tr></thead>
                <tbody>
                  {report.states.map((s) => (
                    <tr key={s.state_code} className="border-b border-black/5 dark:border-white/5" data-testid={`suta-report-row-${s.state_code}`}>
                      <td className="p-2">{s.state_code} <span className="opacity-60">{s.state_name}</span></td>
                      <td className="p-2 text-end">{money(s.taxable)}</td>
                      <td className="p-2 text-end font-medium">{money(s.suta)}</td>
                    </tr>
                  ))}
                  {report.total_futa > 0 && (
                    <tr className="border-b border-black/5 dark:border-white/5" data-testid="suta-report-row-FUTA">
                      <td className="p-2">FUTA <span className="opacity-60">Federal Unemployment</span></td>
                      <td className="p-2 text-end">{money(report.futa.taxable)}</td>
                      <td className="p-2 text-end font-medium">{money(report.futa.amount)}</td>
                    </tr>
                  )}
                  <tr className="font-semibold" data-testid="suta-report-total"><td className="p-2">Total employer tax</td><td className="p-2 text-end">—</td><td className="p-2 text-end">{money(report.total_employer_tax)}</td></tr>
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
